import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Mail,
  Send,
  Linkedin,
  Facebook,
  ShoppingBag,
  Tractor,
  Calendar,
  HardDrive,
  FileSpreadsheet,
  FileText,
  Volume2,
  Globe,
  Link,
  Unlink,
  Settings,
  Check,
  Loader2,
  ExternalLink,
  Play,
  Pause,
  Download,
  Upload,
  Search,
  RefreshCw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Platform configurations
const PLATFORMS = [
  {
    id: 'google',
    name: 'Google',
    description: 'Gmail, Calendar, Drive',
    icon: Mail,
    color: '#ea4335',
    services: ['Gmail', 'Calendar', 'Drive'],
    needsOAuth: true,
  },
  {
    id: 'telegram',
    name: 'Telegram',
    description: 'Notifications et messages',
    icon: Send,
    color: '#0088cc',
    fields: [
      { key: 'bot_token', label: 'Bot Token', placeholder: '123456:ABC-DEF...' },
      { key: 'chat_id', label: 'Chat ID (optionnel)', placeholder: '123456789' }
    ]
  },
  {
    id: 'linkedin',
    name: 'LinkedIn',
    description: 'Profils et publications',
    icon: Linkedin,
    color: '#0077b5',
    fields: [
      { key: 'email', label: 'Email', placeholder: 'votre@email.com' },
      { key: 'password', label: 'Mot de passe', type: 'password' }
    ]
  },
  {
    id: 'facebook',
    name: 'Facebook',
    description: 'Pages, groupes, marketplace',
    icon: Facebook,
    color: '#1877f2',
    fields: [
      { key: 'email', label: 'Email', placeholder: 'votre@email.com' },
      { key: 'password', label: 'Mot de passe', type: 'password' }
    ]
  },
  {
    id: 'leboncoin',
    name: 'Leboncoin',
    description: 'Petites annonces',
    icon: ShoppingBag,
    color: '#ff6e14',
    fields: [
      { key: 'email', label: 'Email', placeholder: 'votre@email.com' },
      { key: 'password', label: 'Mot de passe', type: 'password' }
    ]
  },
  {
    id: 'agriaffaires',
    name: 'Agriaffaires',
    description: 'Matériel agricole',
    icon: Tractor,
    color: '#7cb342',
    noAuth: true,
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'CRM - Synchronisation quotidienne contacts/clients',
    icon: Globe,
    color: '#ff7a59',
    helpText: "Synchronisez vos contacts HubSpot avec Bureau Agricole. Mise à jour automatique quotidienne.",
    fields: [
      { key: 'api_key', label: 'Clé API HubSpot', placeholder: 'pat-eu1-xxx' },
    ],
    syncFrequency: 'quotidienne'
  },
  {
    id: 'sellsy',
    name: 'Sellsy',
    description: 'CRM & Facturation - Synchronisation bidirectionnelle',
    icon: FileText,
    color: '#00a8e8',
    helpText: "Connectez votre Sellsy pour synchroniser clients, devis et factures automatiquement.",
    fields: [
      { key: 'consumer_token', label: 'Consumer Token', placeholder: 'xxx' },
      { key: 'consumer_secret', label: 'Consumer Secret', type: 'password' },
      { key: 'user_token', label: 'User Token', placeholder: 'xxx' },
      { key: 'user_secret', label: 'User Secret', type: 'password' },
    ],
    syncFrequency: 'quotidienne'
  },
  {
    id: 'axonaut',
    name: 'Axonaut',
    description: 'CRM & Gestion - Synchronisation contacts et projets',
    icon: Globe,
    color: '#6366f1',
    helpText: "Synchronisez Axonaut avec Bureau Agricole pour une gestion unifiée de vos clients.",
    fields: [
      { key: 'api_key', label: 'Clé API Axonaut', placeholder: 'xxx' },
    ],
    syncFrequency: 'quotidienne'
  },
  {
    id: 'msa',
    name: 'MSA',
    description: 'Mutualité Sociale Agricole - Cotisations, DSN',
    icon: FileSpreadsheet,
    color: '#0d6938',
    comingSoon: true,
    helpText: "Connexion à votre espace MSA pour la gestion des cotisations et des déclarations sociales nominatives (DSN).",
    fields: [
      { key: 'numero_adherent', label: 'N° Adhérent MSA', placeholder: '0123456789' },
      { key: 'password', label: 'Mot de passe', type: 'password' }
    ]
  },
  {
    id: 'telepac',
    name: 'TelePAC',
    description: 'Déclarations PAC et aides agricoles',
    icon: Globe,
    color: '#003d82',
    comingSoon: true,
    helpText: "Connexion à TelePAC pour le suivi de vos déclarations PAC, RPG et demandes d'aides agricoles.",
    fields: [
      { key: 'numero_pacage', label: 'N° PACAGE', placeholder: '011234567' },
      { key: 'password', label: 'Mot de passe', type: 'password' }
    ]
  },
  {
    id: 'banque',
    name: 'Connexion Bancaire',
    description: 'Synchronisation des comptes bancaires',
    icon: HardDrive,
    color: '#1a237e',
    comingSoon: true,
    helpText: "Connexion sécurisée via Budget Insight/Powens pour synchroniser vos relevés bancaires et automatiser le rapprochement comptable. Conforme DSP2.",
    requiresSetup: true
  },
];

// Credential Dialog Component
const CredentialDialog = ({ platform, isConnected, onConnect, onDisconnect }) => {
  const [open, setOpen] = useState(false);
  const [credentials, setCredentials] = useState({});
  const [saving, setSaving] = useState(false);

  const handleConnect = async () => {
    if (platform.needsOAuth) {
      // Rediriger vers OAuth Google
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/integrations/google/auth-url`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        if (data.auth_url) {
          window.location.href = data.auth_url;
        }
      } catch (error) {
        toast.error('Erreur de connexion Google');
      }
      return;
    }

    setSaving(true);
    await onConnect(platform.id, credentials);
    setSaving(false);
    setOpen(false);
  };

  const Icon = platform.icon;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant={isConnected ? "outline" : platform.comingSoon ? "secondary" : "default"}
          size="sm"
          style={{ backgroundColor: isConnected || platform.comingSoon ? undefined : platform.color }}
        >
          {platform.comingSoon ? (
            <>
              <Calendar className="w-4 h-4 mr-2" />
              Bientôt
            </>
          ) : isConnected ? (
            <>
              <Settings className="w-4 h-4 mr-2" />
              Gérer
            </>
          ) : (
            <>
              <Link className="w-4 h-4 mr-2" />
              Connecter
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5" style={{ color: platform.color }} />
            {platform.name}
          </DialogTitle>
          <DialogDescription>{platform.description}</DialogDescription>
        </DialogHeader>

        {platform.needsOAuth ? (
          <div className="py-4 text-center">
            <p className="text-muted-foreground mb-4">
              Cliquez sur le bouton ci-dessous pour vous connecter avec votre compte Google
            </p>
            <Button onClick={handleConnect} style={{ backgroundColor: platform.color }}>
              <Icon className="w-4 h-4 mr-2" />
              Se connecter avec Google
            </Button>
          </div>
        ) : platform.comingSoon ? (
          <div className="py-4 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-4">
              <Calendar className="w-8 h-8 text-amber-600" />
            </div>
            <Badge className="bg-amber-100 text-amber-800 mb-4">Bientôt disponible</Badge>
            <p className="text-muted-foreground mb-2">
              {platform.helpText || "Cette intégration sera disponible prochainement."}
            </p>
            <p className="text-xs text-muted-foreground">
              Nous travaillons activement sur cette fonctionnalité. Restez informé !
            </p>
          </div>
        ) : platform.noAuth ? (
          <div className="py-4 text-center">
            <Check className="w-12 h-12 mx-auto text-green-500 mb-2" />
            <p className="text-muted-foreground">
              Aucune authentification requise. La recherche est disponible directement.
            </p>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            {platform.fields?.map((field) => (
              <div key={field.key} className="space-y-2">
                <Label>{field.label}</Label>
                <Input
                  type={field.type || 'text'}
                  placeholder={field.placeholder}
                  value={credentials[field.key] || ''}
                  onChange={(e) => setCredentials(prev => ({
                    ...prev,
                    [field.key]: e.target.value
                  }))}
                />
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="flex gap-2">
          {isConnected && !platform.noAuth && (
            <Button variant="destructive" onClick={() => onDisconnect(platform.id)}>
              <Unlink className="w-4 h-4 mr-2" />
              Déconnecter
            </Button>
          )}
          {!platform.needsOAuth && !platform.noAuth && (
            <Button onClick={handleConnect} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// TTS Component
const TTSPlayer = () => {
  const [text, setText] = useState('');
  const [voice, setVoice] = useState('nova');
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);

  const voices = [
    { id: 'alloy', name: 'Alloy', desc: 'Neutre' },
    { id: 'echo', name: 'Echo', desc: 'Masculin grave' },
    { id: 'fable', name: 'Fable', desc: 'Féminin doux' },
    { id: 'onyx', name: 'Onyx', desc: 'Masculin profond' },
    { id: 'nova', name: 'Nova', desc: 'Féminin dynamique' },
    { id: 'shimmer', name: 'Shimmer', desc: 'Féminin chaleureux' },
  ];

  const generateSpeech = async () => {
    if (!text.trim()) return;
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/integrations/tts/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text, voice })
      });

      const data = await response.json();
      if (data.audio_base64) {
        const audioBlob = new Blob(
          [Uint8Array.from(atob(data.audio_base64), c => c.charCodeAt(0))],
          { type: 'audio/mp3' }
        );
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);
        toast.success('Audio généré !');
      }
    } catch (error) {
      toast.error('Erreur génération audio');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Volume2 className="w-5 h-5" />
          Text-to-Speech
        </CardTitle>
        <CardDescription>Faites parler vos agents IA</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Texte à lire</Label>
          <textarea
            className="w-full min-h-[100px] p-3 border rounded-lg resize-none"
            placeholder="Entrez le texte que vous voulez faire lire..."
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label>Voix</Label>
          <div className="grid grid-cols-3 gap-2">
            {voices.map((v) => (
              <button
                key={v.id}
                onClick={() => setVoice(v.id)}
                className={`p-2 rounded-lg border text-left transition-all ${
                  voice === v.id ? 'border-primary bg-primary/10' : 'hover:border-muted-foreground/50'
                }`}
              >
                <p className="font-medium text-sm">{v.name}</p>
                <p className="text-xs text-muted-foreground">{v.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={generateSpeech} disabled={loading || !text.trim()} className="flex-1">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Générer
          </Button>
        </div>

        {audioUrl && (
          <div className="p-4 bg-muted rounded-lg">
            <audio ref={audioRef} controls className="w-full" src={audioUrl} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Web Scraping Component
const WebScraper = () => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const scrape = async () => {
    if (!url.trim()) return;
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/integrations/scrape`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ url, extract_type: 'structured' })
      });

      const data = await response.json();
      setResult(data);
      toast.success('Page analysée !');
    } catch (error) {
      toast.error('Erreur de scraping');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="w-5 h-5" />
          Web Scraping
        </CardTitle>
        <CardDescription>Extraire des informations de n'importe quel site</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            placeholder="https://www.example.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <Button onClick={scrape} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>

        {result && (
          <div className="p-4 bg-muted rounded-lg space-y-2 text-sm max-h-[300px] overflow-auto">
            {result.title && <p><strong>Titre:</strong> {result.title}</p>}
            {result.meta_description && <p><strong>Description:</strong> {result.meta_description}</p>}
            {result.prices?.length > 0 && <p><strong>Prix trouvés:</strong> {result.prices.join(', ')} €</p>}
            {result.phones?.length > 0 && <p><strong>Téléphones:</strong> {result.phones.join(', ')}</p>}
            {result.content && <p className="mt-2 whitespace-pre-wrap">{result.content.substring(0, 500)}...</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Agriaffaires Search Component
const AgriaffairesSearch = () => {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  const categories = [
    { id: 'all', name: 'Tout' },
    { id: 'tracteurs', name: 'Tracteurs' },
    { id: 'moissonneuses', name: 'Moissonneuses' },
    { id: 'pulverisateurs', name: 'Pulvérisateurs' },
    { id: 'semoirs', name: 'Semoirs' },
    { id: 'remorques', name: 'Remorques' },
  ];

  const search = async () => {
    if (!query.trim()) return;
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/integrations/agriaffaires/search?query=${encodeURIComponent(query)}&category=${category}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      setResults(data.annonces || []);
      toast.success(`${data.results_count} résultats trouvés`);
    } catch (error) {
      toast.error('Erreur de recherche');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Tractor className="w-5 h-5 text-green-600" />
          Agriaffaires
        </CardTitle>
        <CardDescription>Recherche de matériel agricole d'occasion</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            placeholder="John Deere 6R..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1"
          />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="px-3 py-2 border rounded-lg"
          >
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          <Button onClick={search} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>

        {results.length > 0 && (
          <div className="space-y-2 max-h-[400px] overflow-auto">
            {results.map((item, idx) => (
              <div key={idx} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{item.title}</p>
                    <div className="flex gap-2 mt-1 text-sm text-muted-foreground">
                      {item.year && <span>{item.year}</span>}
                      {item.hours && <span>• {item.hours}</span>}
                    </div>
                  </div>
                  <Badge variant="secondary" className="text-green-700 bg-green-100">
                    {item.price || 'Prix NC'}
                  </Badge>
                </div>
                {item.url && (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline flex items-center gap-1 mt-2"
                  >
                    Voir l'annonce <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Integrations Page
const IntegrationsPage = () => {
  const [connectedPlatforms, setConnectedPlatforms] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCredentials();
    
    // Check for Google OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('google') === 'connected') {
      toast.success('Google connecté avec succès !');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchCredentials = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/integrations/credentials`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      const connected = {};
      data.platforms?.forEach(p => {
        if (p.configured) connected[p.id] = true;
      });
      setConnectedPlatforms(connected);
    } catch (error) {
      console.error('Erreur chargement credentials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (platformId, credentials) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/integrations/credentials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ platform: platformId, credentials })
      });

      if (response.ok) {
        toast.success('Plateforme connectée');
        setConnectedPlatforms(prev => ({ ...prev, [platformId]: true }));
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const handleDisconnect = async (platformId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/integrations/credentials/${platformId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Plateforme déconnectée');
      setConnectedPlatforms(prev => {
        const updated = { ...prev };
        delete updated[platformId];
        return updated;
      });
    } catch (error) {
      toast.error('Erreur de déconnexion');
    }
  };

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div>
        <h1 className="text-2xl font-bold">Intégrations</h1>
        <p className="text-muted-foreground">
          Connectez vos agents IA à vos outils et plateformes
        </p>
      </div>

      <Tabs defaultValue="platforms" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="platforms">Plateformes</TabsTrigger>
          <TabsTrigger value="tts">Voix TTS</TabsTrigger>
          <TabsTrigger value="scraping">Web Scraping</TabsTrigger>
          <TabsTrigger value="agriaffaires">Agriaffaires</TabsTrigger>
        </TabsList>

        <TabsContent value="platforms" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {PLATFORMS.map((platform) => {
              const Icon = platform.icon;
              const isConnected = connectedPlatforms[platform.id] || platform.noAuth;
              
              return (
                <Card key={platform.id} className="relative">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${platform.color}20` }}
                        >
                          <Icon className="w-6 h-6" style={{ color: platform.color }} />
                        </div>
                        <div>
                          <h3 className="font-semibold">{platform.name}</h3>
                          <p className="text-sm text-muted-foreground">{platform.description}</p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                      <Badge variant={isConnected ? "default" : "secondary"}>
                        {isConnected ? (
                          <><Check className="w-3 h-3 mr-1" /> Connecté</>
                        ) : (
                          'Non connecté'
                        )}
                      </Badge>
                      
                      <CredentialDialog
                        platform={platform}
                        isConnected={isConnected}
                        onConnect={handleConnect}
                        onDisconnect={handleDisconnect}
                      />
                    </div>

                    {platform.services && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {platform.services.map((service) => (
                          <Badge key={service} variant="outline" className="text-xs">
                            {service}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="tts">
          <TTSPlayer />
        </TabsContent>

        <TabsContent value="scraping">
          <WebScraper />
        </TabsContent>

        <TabsContent value="agriaffaires">
          <AgriaffairesSearch />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IntegrationsPage;
